/**
 * Who sees the talk-down control (ENG-2135): the LaunchDarkly flag
 * `showTalkDown` AND the login-time `permission.talk_down` from /user/info/
 * (copied key-by-key into appStore.userPermissions by handleSetPermissions
 * and dataFetchers, so a new permission needs no plumbing beyond its default)
 * AND, for v1, a site whose cameras are reached over RTSP/ONVIF. Admin
 * enforces the permission again on the POST; this only hides the button.
 */
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { useAppStore } from "../../../appStore";
import { siteFamily } from "../../pages/Troubleshooting/TroubleshootingUtils/siteFamily";

export const TALK_DOWN_FLAG = "showTalkDown";
export const TALK_DOWN_PERMISSION = "talk_down";

/**
 * v1 plays audio over the camera's own RTSP/ONVIF backchannel, so only the
 * RTSP-pull family qualifies (admin collapses every is_rtsp integration to
 * "rtsp"; onvif_wireguard is in the same family). Unknown = not yet loaded
 * = hidden, so the control never flashes on a VMS site.
 */
export const talkDownSupportsSite = (
  integrationTypeName: string | null | undefined
): boolean => siteFamily(integrationTypeName) === "rtsp-pull";

export interface TalkDownGate {
  /** The site's integration_type_name, or undefined while unknown. */
  integrationTypeName?: string | null;
  /** Caller cannot know the site type; skip that part of the gate. */
  skipSiteTypeGate?: boolean;
  /**
   * The camera's `talk_down_capable` from the backend (ENG-1721): false =
   * probed and has no speaker, so hidden. true, null (not probed yet) and
   * absent (older backend) all keep today's behaviour, so nothing disappears
   * until the backend actually reports false.
   */
  talkDownCapable?: boolean | null;
}

/** Live push-to-talk (ENG-1721) on top of the v1 gate. */
export const LIVE_TALK_DOWN_FLAG = "showLiveTalkDown";

export type TalkDownMode = "clip" | "live";

/**
 * Which transport the control uses. Live only when `showLiveTalkDown` is
 * on; a caller can pin "clip". Without the flag it is always "clip", so the
 * control is exactly v1. The v1 gate (useTalkDownEnabled) still applies.
 */
export const useTalkDownMode = (requested?: TalkDownMode): TalkDownMode => {
  const { flags } = useFeatureFlag();
  const liveOn = Boolean(flags?.[LIVE_TALK_DOWN_FLAG]);
  if (requested === "clip") return "clip";
  return liveOn ? "live" : "clip";
};

export const useTalkDownEnabled = ({
  integrationTypeName,
  skipSiteTypeGate = false,
  talkDownCapable,
}: TalkDownGate = {}): boolean => {
  const { flags } = useFeatureFlag();
  const { userPermissions } = useAppStore();
  const flagOn = Boolean(flags?.[TALK_DOWN_FLAG]);
  const permitted = Boolean(userPermissions?.[TALK_DOWN_PERMISSION]);
  const siteOk = skipSiteTypeGate || talkDownSupportsSite(integrationTypeName);
  const cameraOk = talkDownCapable !== false;
  return flagOn && permitted && siteOk && cameraOk;
};
